import React from "react";
import axios from "axios";
import { getAuthToken, setAuthToken } from "@/utils/storage";
import {  setSessionExpired } from "@/redux/slice/auth.slice";
import { store } from "@/redux/store/store";
import { showToast } from "@/utils/toastUtils";
import { refreshToken } from "@/middleware/getCookies";
import { createRoot } from "react-dom/client";
// import GlobalLoader from "@/components/common/loader/Loader";
import Loader from "components/loader/Loader";

const axiosConfig = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
    "show-toast": "true" ,
  },
  withCredentials: true,
});

const loadingInterceptor = {
  _loadingCount: 0,
  _root: null,

  start() {
    this._loadingCount++;
    this.renderLoader();
  },

  end() {
    this._loadingCount--;
    if (this._loadingCount <= 0) {
      this._loadingCount = 0;
      this.removeLoader();
    }
  },

  renderLoader() {
    const existingLoader = document.getElementById("global-loader");
    if (!existingLoader) {
      const loaderContainer = document.createElement("div");
      loaderContainer.id = "global-loader";
      document.body.appendChild(loaderContainer);

      this._root = createRoot(loaderContainer);
      // eslint-disable-next-line react/react-in-jsx-scope
      this._root.render(<Loader />);
    }
  },

  removeLoader() {
    const loaderContainer = document.getElementById("global-loader");
    if (loaderContainer) {
      this._root?.unmount();
      document.body.removeChild(loaderContainer);
      this._root = null;
    }
  }
};

axiosConfig.interceptors.request.use(
  (request) => {
   
    if (request.method !== 'get' && request.headers['show-loader'] !== 'false') {
      loadingInterceptor.start();
    }

    const token = getAuthToken();
    if (token) {
      request.headers['Authorization'] = token;
    }

    if (request.data instanceof FormData) {
      request.headers['Content-Type'] = 'multipart/form-data';
    }

    return request;
  },
  error => {
    console.error("Request Error:", error.message);
    loadingInterceptor.end();
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve(token));
  failedQueue = [];
};

const handleSessionExpiration = (originalRequest) => {

  store.dispatch(setSessionExpired());
  if (originalRequest.method !== 'get') {
    showToast({ message: 'Session expired. Please login again.', severity: 'error' });
  }
  return Promise.reject(new Error('Session expired. Please login again.'));
};

const handleTokenRefresh = async (originalRequest) => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(token => {
      originalRequest.headers['Authorization'] = token;
      return axiosConfig(originalRequest);
    }).catch(err => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const { isAuth, role } = store.getState().auth;
    if (!isAuth) {
      return handleSessionExpiration(originalRequest);
    }

    const newAccessToken = await refreshToken(role);
    if (newAccessToken !== null) {
      setAuthToken(newAccessToken);
      axiosConfig.defaults.headers["Authorization"] = newAccessToken;
      originalRequest.headers["Authorization"] = newAccessToken;
      processQueue(null, newAccessToken);
      return axiosConfig(originalRequest);
    } else {
      return handleSessionExpiration(originalRequest);
    }
  } catch (err) {
    processQueue(err, null);
    return handleSessionExpiration(originalRequest);
  } finally {
    isRefreshing = false;
  }
};


axiosConfig.interceptors.response.use(
  response => {
    loadingInterceptor.end();
    if (response.config.responseType === 'blob') {
      return response;
    }
    const showToastMessage = response.config.headers['show-toast'] !== 'false';
    // console.log('config headers ', response.config.headers['show-toast']);

    if (response.data?.message && showToastMessage) {
      showToast({ message: response.data.message, severity: 'success' });
    }
    return response;
  },
  async (error) => {
    loadingInterceptor.end();
    const originalRequest = error.config;
    const showToastMessage = originalRequest.headers['show-toast'] !== 'false';
    
    if (error?.response?.data?.isLogout) {
      return handleSessionExpiration(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      return handleTokenRefresh(originalRequest);
    }

    if (error.response?.data?.errors) {
      return Promise.reject(error.response.data);
    }

    if (error.response) {
      if(error?.response?.data?.mode === "2STEP_AUTH" || error?.response?.data?.mode === "2FA_AUTH" || error?.response?.data?.mode === "RESEND_MAIL" || error?.response?.data?.mode === "CHANGEPASSWORD"){
          return Promise.reject(error.response.data);
      }
      
      const errorMessage = error.response.data?.message || 'An unexpected error occurred.';
      if (originalRequest.method !== 'get' && showToastMessage) {
        switch (error.response.status) {
          case 400:
            showToast({ message: errorMessage || "Invalid request.", severity: 'error' });
            break;
          case 403:
            showToast({ message: errorMessage || "You don't have permission to access this resource.", severity: 'error' });
            break;
          case 404:
            showToast({ message: errorMessage || "The requested resource was not found.", severity: 'error' });
            break;
          case 500:
            showToast({ message: errorMessage || "An error occurred on the server.", severity: 'error' });
            break;
          default:
            showToast({ message: `${errorMessage}`, severity: 'error' });
        }
      }
    } else if (error.request && originalRequest.method !== 'get' && showToastMessage) {

      showToast({ message: "Network Error: Please check your network connection.", severity:'error' });
    } else if (originalRequest.method !== 'get' && showToastMessage) {

      showToast({ message: `Error: ${error.message}`, severity: 'error' });
    }

    // Special handling for blob request errors
    if (originalRequest.responseType === 'blob' && error.response) {
      const blob = error.response.data;
      const text = await new Response(blob).text();
      try {
        error.response.data = JSON.parse(text);
      } catch (e) {
        error.response.data = { message: text };
      }
    }

    return Promise.reject(error);
  }
);



export default axiosConfig;